// RDx Decks V2 — the slide-kind content model.
//
// A slide IS a `kind` plus its fields (the prototype's flatter model), in
// contrast to the legacy block model (deck-model.ts, slides[].blocks[]). This
// is the v2 source of truth: generation emits it, the renderer (render-slides.ts)
// compiles it to self-contained `.slide` markup, the Studio edits it via stable
// ids + data-region, and the presenter reveals it via slideSteps().
//
// Ported verbatim in shape from the prototype `slide-render.jsx` Slide() field
// access + `data.jsx`. Field names are the data-region ids and MUST NOT change
// (they are the edit contract). All slides carry a stable `id`.
//
// This file is ADDITIVE — it does not replace deck-model.ts yet; the two
// coexist until the Phase 8 swap, so the existing block pipeline keeps building.

/** Brand-tinted abstract art placeholder palette (Art layer). */
export type ArtKind = 'slate' | 'sage' | 'dawn' | 'mist' | 'dusk' | 'ember'
  | 'ocean' | 'plum' | 'gold' | 'forest';

/** Slide variant skins (background/ink treatment). Deprecated in favour of the
 *  `skin` + `layout` axes; retained as a back-compat alias (see normaliseSlide). */
export type SlideVariant = '' | 's-ink' | 's-surface' | 'editorial';

/** Background/ink skin, separated from geometry (layout). */
export type SlideSkin = '' | 's-ink' | 's-surface';

/** The font axis (mirrors tokens.css [data-font]). Pruned to the two loaded
 *  voices this pass; Graphik/Museo land once self-hosted. */
export type FontAxis = 'inter' | 'editorial';

/** Slide background treatment — one unified layer applied to ANY kind. */
export type BgType = 'none' | 'colour' | 'gradient' | 'image' | 'image-split' | 'motion' | 'video';
export interface SlideBackground {
  type: BgType;
  art?: ArtKind;            // gradient/motion: which named gradient
  img?: string;             // image/image-split: data-URL or allow-listed src
  videoId?: string;         // video: the 11-char YouTube id (the id ONLY, never a raw URL)
  side?: 'left' | 'right';  // image-split: which side the media sits
  // Legibility scrim over media backgrounds: 'none' (no veil) | 'bottom'
  // (gradient up from the foot) | 'centre' (radial darken under centred text) |
  // 'strong' (full-coverage darken). UNSET → the renderer DEFAULTS image/
  // image-split backgrounds on text-bearing kinds to 'strong'; gradients/video
  // keep the legacy bottom veil. See render-slides.ts scrimClass().
  scrim?: 'bottom' | 'centre' | 'strong' | 'none';
  tint?: number;            // colour: brand-wash strength 0–100
  // Focal point of the image subject, in PERCENT (0–100, x left→right, y
  // top→bottom). Threaded into the media layer as `background-position` so an
  // off-centre subject can be reframed without a re-crop. Clamped 0–100;
  // unset → centre (50/50). image/image-split only.
  focal?: { x: number; y: number };
}

/** Per-slide visibility toggles (e.g. title on/off). */
export interface SlideShow { title?: boolean; eyebrow?: boolean; footer?: boolean }

/** Accent meaning colours used by some kinds. */
export type SlideAccent = 'emerald' | 'amber' | 'sky' | 'rose' | 'violet';

/** The 18 slide kinds (the handover's "14" + quote/quote-image/full-bleed/split
 *  split out). `kind` selects the layout branch in render-slides.ts. */
export type SlideKind =
  | 'cover'
  | 'full-bleed'
  | 'quote-image'
  | 'quote'
  | 'split'
  | 'gallery'
  | 'agenda'
  | 'stat-row'
  | 'bignum'
  | 'kpi'
  | 'trend'
  | 'chart'
  | 'pricing'
  | 'timeline'
  | 'compare'
  | 'features'
  | 'bullets'
  | 'cta';

export const SLIDE_KINDS = [
  'cover', 'full-bleed', 'quote-image', 'quote', 'split', 'gallery', 'agenda',
  'stat-row', 'bignum', 'kpi', 'trend', 'chart', 'pricing', 'timeline',
  'compare', 'features', 'bullets', 'cta',
] as const satisfies readonly SlideKind[];

// ── Per-kind nested shapes ────────────────────────────────────────────────
export interface Stat { n: string; accent?: string; label: string }
export interface AgendaItem { t: string; n: string; active?: boolean }
export interface GalleryItem { art?: ArtKind; img?: string; t: string; n?: string }
export interface FeatureCard { icon: 'home' | 'trending' | 'shield'; h: string; d: string }
export interface Kpi { label: string; value: string; delta: string; up?: boolean; spark?: number[] }
export interface TrendPoint { x: string; v: number; note?: string }
export interface ChartBar { k: string; v: number; note?: string }
export interface PriceTier {
  name: string; price: string; period?: string; note?: string;
  features: string[]; featured?: boolean; tag?: string;
}
/** A single feature-comparison row for the pricing `table` layout: a row label
 *  plus one cell per tier (in tier order). A boolean cell renders a tick/cross;
 *  a string cell renders editable text. */
export interface PricingMatrixRow { label: string; cells: (boolean | string)[] }
export interface Milestone { date: string; t: string; n?: string; done?: boolean; active?: boolean }
export interface CompareColumn { tag?: string; title: string; items: string[]; highlight?: boolean }

/** Fields shared by all slides. NB: the new layout/background/show/skin/font/
 *  footer fields are ALL OPTIONAL — a slide without them renders exactly as
 *  before (normaliseSlide derives them from legacy fields at load). */
interface SlideBase {
  id: string;                 // stable id — edits, analytics, reorder key off this
  kind: SlideKind;
  label: string;              // rail / footer tag, e.g. "04 · Adoption"
  variant?: SlideVariant;     // DEPRECATED skin alias (see normaliseSlide)
  eyebrow?: string;
  title?: string;
  layout?: string;            // named layout id from KIND_META[kind].layouts
  background?: SlideBackground;
  show?: SlideShow;           // per-slide visibility toggles
  skin?: SlideSkin;           // background/ink skin, separate from layout geometry
  font?: FontAxis;            // rare per-slide font override (deck-level is the norm)
  theme?: string;             // rare per-slide theme override (deck-level is the norm)
  footer?: string;            // footer text; empty/undefined falls back to the default
}

// ── The discriminated union (one interface per kind) ──────────────────────
export interface CoverSlide extends SlideBase {
  kind: 'cover';
  coverStyle?: 'classic' | 'statement' | 'editorial';
  art?: ArtKind; img?: string; align?: 'center';
  foot?: string; sub?: string; byline?: string;
}
export interface FullBleedSlide extends SlideBase {
  kind: 'full-bleed';
  art?: ArtKind; img?: string; animate?: boolean; align?: 'center'; editorial?: boolean; lede?: string;
}
export interface QuoteImageSlide extends SlideBase {
  kind: 'quote-image';
  art?: ArtKind; img?: string; animate?: boolean; quote: string; name?: string; meta?: string; avatar?: string;
}
export interface QuoteSlide extends SlideBase {
  kind: 'quote';
  quote: string; name?: string; meta?: string; avatar?: string;
}
export interface SplitSlide extends SlideBase {
  kind: 'split';
  side?: 'left' | 'right'; art?: ArtKind; img?: string; animate?: boolean; caption?: string; lede?: string; items?: string[];
}
export interface GallerySlide extends SlideBase { kind: 'gallery'; items: GalleryItem[] }
export interface AgendaSlide extends SlideBase { kind: 'agenda'; items: AgendaItem[]; panelImg?: string; panelArt?: ArtKind }
export interface StatRowSlide extends SlideBase { kind: 'stat-row'; lede?: string; stats: Stat[] }
export interface BignumSlide extends SlideBase {
  kind: 'bignum'; lede?: string; value: string; vlabel: string; delta: string; down?: boolean;
}
export interface KpiSlide extends SlideBase { kind: 'kpi'; lede?: string; kpis: Kpi[]; panelImg?: string; panelArt?: ArtKind }
export interface TrendSlide extends SlideBase {
  kind: 'trend'; lede?: string; points: TrendPoint[]; prefix?: string; unit?: string; min?: number; max?: number; source?: string;
}
export interface ChartSlide extends SlideBase { kind: 'chart'; lede?: string; bars: ChartBar[]; unit?: string; source?: string }
export interface PricingSlide extends SlideBase {
  kind: 'pricing'; lede?: string; tiers: PriceTier[];
  matrix?: PricingMatrixRow[];   // when present + layout==='table', render a true feature-comparison matrix
}
export interface TimelineSlide extends SlideBase { kind: 'timeline'; milestones: Milestone[] }
export interface CompareSlide extends SlideBase { kind: 'compare'; left: CompareColumn; right: CompareColumn }
export interface FeaturesSlide extends SlideBase { kind: 'features'; cards: FeatureCard[] }
export interface BulletsSlide extends SlideBase { kind: 'bullets'; lede?: string; items: string[]; panelImg?: string; panelArt?: ArtKind }
export interface CtaSlide extends SlideBase {
  kind: 'cta'; headline: string; sub?: string; contact: [string, string][]; art?: ArtKind; img?: string;
  panelImg?: string; panelArt?: ArtKind;
}

export type Slide =
  | CoverSlide | FullBleedSlide | QuoteImageSlide | QuoteSlide | SplitSlide | GallerySlide
  | AgendaSlide | StatRowSlide | BignumSlide | KpiSlide | TrendSlide | ChartSlide
  | PricingSlide | TimelineSlide | CompareSlide | FeaturesSlide | BulletsSlide | CtaSlide;

/** Customer-branding ("brand kit") for a deck — Phase 3. A LOGO + a custom
 *  ACCENT + an optional surface TINT, NOT a full palette. Every field is
 *  optional, so a deck WITHOUT a brandKit renders exactly as before. Logos are
 *  user-supplied and pass through the renderer's srcAttr allow-list (`/`,
 *  `http(s):`, `data:image/`); the accent through safeHex; the tint is clamped
 *  0–100. The co-brand lockup defaults ON ("RICOH RDx × <name>"). */
export interface DeckBrand {
  logo?: string;        // cover/footer logo: data-URL or allow-listed src (via srcAttr)
  logoDark?: string;    // optional alternate logo for dark themes / media scrims
  name?: string;        // customer name — labels the logo + drives the co-brand lockup
  accent?: string;      // hex; HIGHLIGHTS only (eyebrows, bars, key figures). Never the background.
  text?: string;        // hex; optional manual text/ink colour (else auto-contrast per theme/background)
  bg?: string;          // hex; optional background (slide canvas) colour override; else the theme canvas
  coBrand?: boolean;    // default true → "RICOH RDx × <name>" lockup; false → customer logo only
}

export interface DeckMeta {
  title: string;
  subtitle?: string;
  author?: string;
  date?: string;
  theme?: string;        // data-theme value (workplace-paper | workplace-slate | warm | …)
  brand?: string;        // --brand-color ink, e.g. "#00A76F"
  fontset?: FontAxis;    // data-font value; deck-level font (default from the theme bundle)
  brandKit?: DeckBrand;  // optional customer branding (logo + accent + tint); see DeckBrand
}

/** The v2 deck: meta + ordered kind-slides + optional per-slide speaker notes
 *  (keyed by slide id). */
export interface SlideDeck {
  schemaVersion: 2;
  meta: DeckMeta;
  slides: Slide[];
  notes?: Record<string, string>;   // slideId -> speaker note
  // Inline-edit overrides (slideId -> regionId -> plain text). Persisted WITH
  // the model so a saved edit survives a reload (the HTML artifact bakes them
  // in, but the editor reloads from the model). Applied on top of the fields.
  overrides?: Record<string, Record<string, string>>;
  // Rich-text region styles (slideId -> regionId -> { h, align }). A parallel
  // channel to overrides — heading size + alignment applied as rt-* classes by
  // the renderer. Persisted WITH the model alongside overrides so a style-only
  // edit survives a reload and bakes into the artifact. (Shape mirrors
  // render-slides' DeckRegionStyles; declared inline to keep slide-model free of
  // a render-slides import — the dependency runs the other way.)
  regionStyles?: Record<string, Record<string, { h?: number; align?: 'left' | 'center' | 'right' }>>;
}

// ── back-compat + validation ──────────────────────────────────────────────
// The renderer/studio must accept stored decks + current AI output (which carry
// the legacy coverStyle/side/align/variant/top-level art+img and no layout/
// background/skin) and render them unchanged. normaliseSlide derives the new
// optional fields from the legacy ones so the rest of the system reads ONE shape.
// It is purely additive — it never drops or renames a field, so inline-edit
// overrides (keyed by region id) are untouched.

const FONT_AXES: ReadonlySet<string> = new Set(['inter', 'editorial']);
/** Validate a font axis; default 'inter'. */
export function safeFont(f?: string): FontAxis {
  return f && FONT_AXES.has(f) ? (f as FontAxis) : 'inter';
}

const BG_TYPES: ReadonlySet<string> = new Set(['none', 'colour', 'gradient', 'image', 'image-split', 'motion', 'video']);
/** Validate a background type; default 'none'. */
export function safeBgType(t?: string): BgType {
  return t && BG_TYPES.has(t) ? (t as BgType) : 'none';
}

/** Parse a YouTube id out of any common URL form, returning the bare 11-char id
 *  (the model stores the id, NEVER a raw URL). Accepts:
 *    • youtu.be/<id>
 *    • youtube.com/watch?v=<id>  (and the &v= / extra-param variants)
 *    • youtube.com/embed/<id>  ·  youtube.com/shorts/<id>  ·  /v/<id>
 *    • youtube-nocookie.com/embed/<id>
 *    • a bare 11-char id pasted on its own
 *  Returns '' when no valid id is found, so a bad paste never sets a videoId.
 *  A YouTube id is exactly 11 chars from [A-Za-z0-9_-]. */
const YT_ID_RE = /^[A-Za-z0-9_-]{11}$/;
export function youtubeId(url?: string): string {
  const raw = String(url ?? '').trim();
  if (!raw) return '';
  // A bare id pasted on its own.
  if (YT_ID_RE.test(raw)) return raw;
  // watch?v=<id> (or any query carrying v=<id>).
  const vMatch = /[?&]v=([A-Za-z0-9_-]{11})(?:[&#]|$)/.exec(raw);
  if (vMatch) return vMatch[1];
  // youtu.be/<id>, /embed/<id>, /shorts/<id>, /v/<id> path forms.
  const pathMatch = /(?:youtu\.be\/|\/embed\/|\/shorts\/|\/v\/)([A-Za-z0-9_-]{11})(?:[/?&#]|$)/.exec(raw);
  if (pathMatch) return pathMatch[1];
  return '';
}

/** Validate a layout id against the kind's catalogue; default the kind's first
 *  (current-look) layout. Imported lazily to avoid a model↔catalogue import
 *  cycle — the caller passes the allowed list. */
export function safeLayout(layout: string | undefined, allowed: readonly string[]): string {
  return layout && allowed.includes(layout) ? layout : (allowed[0] || '');
}

/** A safe, empty CompareColumn for a `compare` slide whose `left`/`right` is
 *  missing (a partial slide from a generation error). Renders a blank column
 *  rather than throwing on `c.title`/`c.items`. */
const EMPTY_COMPARE_COLUMN: CompareColumn = { tag: '', title: '', items: [] };

/** Map a slide's legacy fields onto the new optional axes WITHOUT mutating the
 *  input. Idempotent: a slide already carrying new fields passes through.
 *
 *  ALSO backfills each kind's REQUIRED array (and compare's left/right columns)
 *  to a safe empty value when missing, so a PARTIAL slide — one emitted during a
 *  generation error without its content array — renders degraded (empty) instead
 *  of throwing on a `.length`/`.map` access and breaking the studio canvas. This
 *  backfill is additive: it never drops or renames a field, and a complete slide
 *  passes through untouched (the array is already present, so we keep it). */
export function normaliseSlide<T extends Slide>(s: T): T {
  const legacy = s as Slide & {
    coverStyle?: string; side?: 'left' | 'right'; align?: string;
    art?: ArtKind; img?: string; animate?: boolean; variant?: SlideVariant;
  };
  const out = { ...s } as T & {
    layout?: string; background?: SlideBackground; skin?: SlideSkin;
  };
  // skin ← variant (s-ink/s-surface are skins; 'editorial' becomes a layout below)
  if (out.skin === undefined && legacy.variant && legacy.variant !== 'editorial') {
    out.skin = legacy.variant as SlideSkin;
  }
  // layout ← coverStyle / align / editorial variant (only when not already set)
  if (out.layout === undefined) {
    if (legacy.coverStyle) out.layout = legacy.coverStyle;            // cover: classic/statement/editorial
    else if (legacy.variant === 'editorial') out.layout = 'editorial';
    else if (legacy.align === 'center') out.layout = 'centred';
  }
  // background ← top-level art/img (cover/full-bleed/quote-image/split/cta/gallery)
  if (out.background === undefined && (legacy.art || legacy.img)) {
    out.background = { type: legacy.img ? 'image' : 'gradient', art: legacy.art, img: legacy.img };
  }
  // Backfill the kind's required array (or compare's columns) to a safe empty
  // value when missing — guards a partial slide against a render-time throw.
  // `o` is the loosely-typed view we patch through (each branch's field is
  // typed on its own kind interface, not on the union). Only assign when the
  // value is actually missing, so a populated slide is byte-identical.
  const o = out as unknown as Record<string, unknown>;
  const arr = (key: string): void => { if (!Array.isArray(o[key])) o[key] = []; };
  switch (out.kind) {
    case 'agenda': arr('items'); break;
    case 'bullets': arr('items'); break;
    // split.items is OPTIONAL — a split with no items legitimately renders no
    // bullet list, so DON'T conjure an empty array (that would flip the renderer's
    // `s.items ?` guard and emit an empty <ul>). Only repair a present-but-broken
    // (non-array) value so a malformed items field can't throw on .map.
    case 'split': if ('items' in o && !Array.isArray(o.items)) o.items = []; break;
    case 'gallery': arr('items'); break;
    case 'stat-row': arr('stats'); break;
    case 'features': arr('cards'); break;
    case 'kpi': arr('kpis'); break;
    case 'trend': arr('points'); break;
    case 'chart': arr('bars'); break;
    case 'pricing': arr('tiers'); break;        // matrix stays optional (undefined is fine)
    case 'timeline': arr('milestones'); break;
    case 'cta': arr('contact'); break;          // contact rows: [key,value][]
    case 'compare':
      if (!o.left || typeof o.left !== 'object') o.left = { ...EMPTY_COMPARE_COLUMN };
      if (!o.right || typeof o.right !== 'object') o.right = { ...EMPTY_COMPARE_COLUMN };
      break;
    default: break;
  }
  return out;
}

/** How many progressive build steps a slide reveals in present mode (0 = the
 *  whole slide reveals at once). Ported verbatim from slide-render.jsx:28.
 *  Every array `.length` is guarded with `(… || [])` so a PARTIAL slide (one
 *  missing its required array) yields 0 steps instead of throwing — the same
 *  resilience the renderer relies on. */
export function slideSteps(slide: Slide): number {
  switch (slide.kind) {
    case 'agenda': return (slide.items || []).length;
    case 'stat-row': return (slide.stats || []).length;
    case 'bignum': return 2;
    case 'features': return (slide.cards || []).length;
    case 'bullets': return (slide.items || []).length;
    case 'quote': return 1;
    case 'chart': return 1;
    case 'gallery': return (slide.items || []).length;
    case 'kpi': return (slide.kpis || []).length;
    case 'pricing': return (slide.tiers || []).length;
    case 'timeline': return (slide.milestones || []).length;
    case 'compare': return 2;
    case 'trend': return 1;
    default: return 0;
  }
}
